import { LABEL_MAX_LENGTH, LABELS_MAX } from '@servicetim/shared';
import { X } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { inputClassName } from '../../ui/input';
import type { FieldControlProps } from '../field-control';

/**
 * `tags` (and the system `labels` field): free-text chips. Enter, comma or Tab adds the typed
 * value; Backspace on an empty input removes the last chip.
 */
export function TagsControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  const [draft, setDraft] = useState('');
  const tags = Array.isArray(value) ? (value as string[]) : [];
  const max = field.config?.type === 'tags' ? field.config.maxTags : LABELS_MAX;
  const readOnly = disabled || field.readOnly;

  const commit = () => {
    const next = draft.trim().slice(0, LABEL_MAX_LENGTH);
    if (next.length === 0) return;
    setDraft('');
    if (tags.includes(next) || tags.length >= max) return;
    onChange([...tags, next]);
  };

  const remove = (tag: string) => {
    const next = tags.filter((t) => t !== tag);
    onChange(next.length === 0 ? undefined : next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || (event.key === 'Tab' && draft.length > 0)) {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && draft.length === 0 && tags.length > 0) {
      remove(tags[tags.length - 1] as string);
    }
  };

  return (
    <div
      className={cn(
        inputClassName,
        'h-auto min-h-9 flex-wrap gap-1 py-1',
        invalid && 'border-danger',
        readOnly && 'bg-surface-sunken',
      )}
    >
      {tags.map((tag) => (
        <Badge key={tag} variant="secondary">
          {tag}
          {readOnly ? null : (
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              className="ml-0.5 rounded-sm hover:bg-border-strong"
              onClick={() => remove(tag)}
            >
              <X className="size-3" />
            </button>
          )}
        </Badge>
      ))}
      <input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          commit();
          onBlur?.();
        }}
        disabled={readOnly}
        placeholder={tags.length === 0 ? (field.placeholder ?? 'Type and press Enter') : ''}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        aria-required={field.required || undefined}
        className="min-w-24 flex-1 bg-transparent outline-none placeholder:text-fg-muted disabled:cursor-not-allowed"
      />
    </div>
  );
}
